/* eslint-disable @typescript-eslint/no-this-alias */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Deletable,
  Identifiable,
  Factory,
  HasCountryCode,
  Organization,
  Fault,
  AwaitingSubject,
  SubscriberSentinel,
  Subscription,
  User,
  Story,
  CountryCode,
  VehicleBrand,
  VehicleModel,
  ProductTemplate,
  Product,
  NewUserResponse,
  Discount,
  ProductStatus,
  ExtraPhoneVerification,
  LanguageCode,
  PaymentMethod,
  Currency,
  Payment,
  OrganizationProductData,
  DiscountTemplate,
  EmailStatus,
  AddVehicleExtraRequest,
  CancellationReason,
  ProductRegretPreview,
  ProductRegretSettlement,
  ProductSource,
} from '../../_models';
import { NewUserExtra } from 'src/app/_models/class/new-user-extra';
import { Interest, Vehicle, VehicleProduct, VikingObject, VikingObjectType } from 'src/app/_models/class/viking-object';
import { OBJECT_STORE_CONFIGS } from 'src/app/_models/class/db-config';
import {
  OrganizationPollErrorResponse,
  OrganizationPollRequestType,
  OrganizationPollResponse,
  OrganizationPollResponseType,
} from './cache-utilites/organization-poll';
import { OrganizationConfigService } from '../organization/organization-config.service';
import { OrganizationEnum } from 'src/app/_models/enum/organization-enum';
import { PaymentMethodAction } from 'src/app/_models/class/payment-method-action';
import {
  CustomerOrganizationPollErrorResponse,
  CustomerOrganizationPollRequestType,
  CustomerOrganizationPollResponse,
  CustomerOrganizationPollResponseType,
} from './cache-utilites/customer-organization-poll';
import { CustomerOrganizationCreate } from 'src/app/_models/interface/customer-organization-create';
import { LookupUserResultDto } from 'src/app/_models/interface/lookup-user-result';
import { VehicleLookup } from 'src/app/_models/interface/vehicle-lookup';
import { CreateUserRequest } from 'src/app/_models/interface/create-user-request';
import { BusinessCustomerRequestPayload } from 'src/app/_models/interface/business-signup-request';
import { DiscountEligibilityType } from 'src/app/_models/class/discount-eligibility';
import { ProductOwnerRefType } from 'src/app/_models/class/product/product-owner';

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  //#region Fields

  private readonly apiUrl: string = environment.apiUrl;
  private readonly acceptHeader: string = environment.acceptHeader;

  private db?: IDBDatabase;
  private readonly dbName: string = 'VikingWebShopDB';
  private readonly dbVersion: number = environment.dbVersion;

  private userPoll: Worker;
  private cache?: UserCache;
  private organizationPoll: Worker;
  private organizationCache?: OrganizationCache;
  private customerOrganizationPoll: Worker;
  private customerOrganizationCache?: CustomerOrganizationCache;
  private forceCustomerOrganizationPollResolve: (() => void) | null = null;

  private users$: AwaitingSubject<User>;
  private discounts$: AwaitingSubject<Discount>;
  private organizations$: AwaitingSubject<Organization>;
  private stories$: AwaitingSubject<Story>;
  private vehicleBrands$: AwaitingSubject<VehicleBrand>;
  private vehicleModels$: AwaitingSubject<VehicleModel>;
  private productTemplates$: AwaitingSubject<ProductTemplate>;
  private products$: AwaitingSubject<Product>;
  private paymentMethods$: AwaitingSubject<PaymentMethod>;
  private objects$: AwaitingSubject<VikingObject>;
  private payments$: AwaitingSubject<Payment>;

  //#endregion

  //#region Initialization

  constructor(
    private http: HttpClient,
    private organizationConfig: OrganizationConfigService,
  ) {
    this.discounts$ = new AwaitingSubject();
    this.users$ = new AwaitingSubject();
    this.organizations$ = new AwaitingSubject();
    this.stories$ = new AwaitingSubject();
    this.vehicleBrands$ = new AwaitingSubject();
    this.vehicleModels$ = new AwaitingSubject();
    this.productTemplates$ = new AwaitingSubject();
    this.products$ = new AwaitingSubject();
    this.paymentMethods$ = new AwaitingSubject();
    this.objects$ = new AwaitingSubject();
    this.payments$ = new AwaitingSubject();

    const self = this;

    const request = window.indexedDB.open(this.dbName, this.dbVersion);

    request.onerror = (event: any) => {
      console.error('IndexedDB error:', event.target.errorCode);
    };

    request.onsuccess = () => {
      this.db = request.result;
      console.log('IndexedDB initialized successfully');
    };

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      this.db = request.result;
      const oldVersion = event.oldVersion;
      const newVersion = this.dbVersion;
      console.log(`Upgrading database from version ${oldVersion} to ${newVersion}`);

      const existingStores = Array.from(this.db.objectStoreNames);

      OBJECT_STORE_CONFIGS.forEach((storeConfig) => {
        if (storeConfig.version <= newVersion && !existingStores.includes(storeConfig.name)) {
          const objectStore = this.db!.createObjectStore(storeConfig.name, { keyPath: storeConfig.keyPath });
          if (storeConfig.indexes) {
            storeConfig.indexes.forEach((index) => {
              objectStore.createIndex(index.name, index.keyPath, index.options);
            });
          }
        }
      });
    };

    // User Poll
    this.userPoll = new Worker(new URL('./../../_workers/user-poll.worker', import.meta.url));
    this.userPoll.onmessage = async function (e) {
      await self.isReady(); // Make sure db is ready for processing poll.

      const t = e.data.type as UserPollResponseType;
      switch (t) {
        case UserPollResponseType.TokenRefresh:
          if (self.cache) {
            self.cache.accessToken = e.data.data;
            localStorage.setItem('accessToken', self.cache.accessToken); // Persist.
            // Forward refreshed token to customer organization poll so it can recover from 401s.
            if (self.customerOrganizationCache) {
              self.customerOrganizationPoll.postMessage({
                type: CustomerOrganizationPollRequestType.TokenRefresh,
                accessToken: self.cache.accessToken,
              });
            }
          }
          break;
        case UserPollResponseType.Error: {
          const m = e.data.data as UserPollErrorResponse;
          console.log('Error received from user poll worker: ' + m.text);
          break;
        }
        case UserPollResponseType.Poll: {
          const uc = self.cache;
          if (uc) {
            const d = e.data.data as UserPollResponse;
            if (d.data) {
              if (d.data.user) {
                await self.parsePollData(uc, [d.data.user], User, self.users$);
              }
              if (d.data.products) {
                await self.parsePollData(uc, d.data.products, Product, self.products$);
              }
              if (d.data.paymentMethods) {
                await self.parsePollData(uc, d.data.paymentMethods, PaymentMethod, self.paymentMethods$);
              }
              if (d.data.objects) {
                await self.parsePollData(uc, d.data.objects, VikingObject, self.objects$);
              }
              if (d.data.payments) {
                await self.parsePollData(uc, d.data.payments, Payment, self.payments$);
              }
              if (d.data.discounts) {
                await self.parsePollData(uc, d.data.discounts, Discount, self.discounts$);
              }

              localStorage.setItem('userCursor' + uc.userId, d.data.etag);

              if (d.done) {
                uc.setFinalInitialPollPackage(d.id);
              }

              uc.setReceivedInitialPollPackage(d.id);
            }
          }
          break;
        }
        case UserPollResponseType.ConnectionOk:
          console.log('User connection is ok.');
          break;
        case UserPollResponseType.ConnectionError:
          console.log('User connection is down.');
          break;
        default:
          throw new Error('Received unrecognized reponse type from user poll.');
      }
    };

    // Organization Poll
    this.organizationPoll = new Worker(new URL('./../../_workers/organization-poll.worker', import.meta.url));
    this.organizationPoll.onmessage = async function (e) {
      await self.isReady(); // Make sure db is ready for processing poll.

      const t = e.data.type as OrganizationPollResponseType;
      switch (t) {
        case OrganizationPollResponseType.Error: {
          const m = e.data.data as OrganizationPollErrorResponse;
          console.log('Error received from organization poll worker: ' + m.text);
          break;
        }
        case OrganizationPollResponseType.Poll: {
          const d = e.data.data as OrganizationPollResponse;

          const oc = self.organizationCache;
          if (oc) {
            if (d.data) {
              await self.parsePollData(oc, d.data.stories, Story, self.stories$);
              await self.parsePollData(oc, d.data.vehicleBrands, VehicleBrand, self.vehicleBrands$);
              await self.parsePollData(oc, d.data.vehicleModels, VehicleModel, self.vehicleModels$);
              await self.parsePollData(oc, d.data.productTemplates, ProductTemplate, self.productTemplates$);
              await self.parsePollData(oc, d.data.users, User, self.users$);

              localStorage.setItem('organizationCursor' + oc.organizationId, d.data.etag);
            }
            if (d.done) {
              oc.setFinalInitialPollPackage(d.id);
            }
            oc.setReceivedInitialPollPackage(d.id);
          }
          break;
        }
        case OrganizationPollResponseType.ConnectionOk:
          console.log('Organization connection is ok.');
          break;
        case OrganizationPollResponseType.ConnectionError:
          console.log('Organization connection is down.');
          break;
        default:
          throw new Error('Received unrecognized reponse type from Organization poll.');
      }
    };

    // Customer Organization Poll
    this.customerOrganizationPoll = new Worker(
      new URL('./../../_workers/customer-organization-poll.worker', import.meta.url),
    );
    this.customerOrganizationPoll.onmessage = async function (e) {
      await self.isReady(); // Make sure db is ready for processing poll.

      const t = e.data.type as CustomerOrganizationPollResponseType;
      switch (t) {
        case CustomerOrganizationPollResponseType.Error: {
          const m = e.data.data as CustomerOrganizationPollErrorResponse;
          console.log('Error received from customer organization poll worker: ' + m.text);
          break;
        }
        case CustomerOrganizationPollResponseType.Poll: {
          const d = e.data.data as CustomerOrganizationPollResponse;

          const oc = self.customerOrganizationCache;
          if (oc) {
            if (d.data) {
              if (d.data.organization) {
                const org = Organization.getFactory().make(d.data.organization);
                oc.setOrganization(org);
                await self.putEntity(org, Organization.getFactory());
              }
              await self.parsePollData(oc, d.data.members, User, self.users$);
              await self.parsePollData(oc, d.data.objects, VikingObject, self.objects$);
              await self.parsePollData(oc, d.data.payments, Payment, self.payments$);
              await self.parsePollData(oc, d.data.discounts, Discount, self.discounts$);
              await self.parsePollData(oc, d.data.products, Product, self.products$);
              await self.parsePollData(oc, d.data.paymentMethods, PaymentMethod, self.paymentMethods$);

              localStorage.setItem('customerOrganizationCursor' + oc.customerOrganizationId, d.data.etag);
            }
            if (d.done) {
              oc.setFinalInitialPollPackage(d.id);
            }
            oc.setReceivedInitialPollPackage(d.id);
            if (self.forceCustomerOrganizationPollResolve) {
              self.forceCustomerOrganizationPollResolve();
              self.forceCustomerOrganizationPollResolve = null;
            }
          }
          break;
        }
        case CustomerOrganizationPollResponseType.ConnectionOk:
          console.log('Customer organization connection is ok.');
          break;
        case CustomerOrganizationPollResponseType.ConnectionError:
          console.log('Customer organization connection is down.');
          break;
        default:
          throw new Error('Received unrecognized reponse type from Customer organization poll.');
      }
    };

    // Initiate cache and start polling if local storage has data from previous login.
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const userId = localStorage.getItem('userId');

    if (accessToken !== null && refreshToken !== null && userId !== null) {
      const c = new UserCache(accessToken, refreshToken, userId);
      this.cache = c;

      // Start User poll.
      this.userPoll.postMessage({
        type: UserPollRequestType.Signin,
        apiUrl: this.apiUrl,
        acceptHeader: this.acceptHeader,
        accessToken: accessToken,
        refreshToken: refreshToken,
        userId: userId,
        etag: localStorage.getItem('userCursor' + userId) ?? '',
      });

      // Start Organization poll.
      const organizationId = localStorage.getItem('organizationId');
      if (organizationId) {
        const organizationCache = new OrganizationCache(organizationId);
        this.organizationCache = organizationCache;

        this.organizationPoll.postMessage({
          type: OrganizationPollRequestType.Signin,
          apiUrl: this.apiUrl,
          acceptHeader: this.acceptHeader,
          organizationId: organizationId,
          etag: localStorage.getItem('organizationCursor' + organizationId) ?? '',
        });
      }

      // Start Customer Organization poll.
      const customerOrganizationId = localStorage.getItem('customerOrganizationId');

      if (customerOrganizationId) {
        const customerOrganizationCache = new CustomerOrganizationCache(customerOrganizationId);
        this.customerOrganizationCache = customerOrganizationCache;

        this.customerOrganizationPoll.postMessage({
          type: CustomerOrganizationPollRequestType.Signin,
          apiUrl: this.apiUrl,
          acceptHeader: this.acceptHeader,
          accessToken: accessToken,
          customerOrganizationId: customerOrganizationId,
          etag: localStorage.getItem('customerOrganizationCursor' + customerOrganizationId) ?? '',
        });
      }
    }
  }

  //#endregion

  //#region Polling

  async parsePollData<T>(
    cache: UserCache | OrganizationCache | CustomerOrganizationCache,
    data: T[],
    classIdentifier: any,
    observable: AwaitingSubject<T>,
  ) {
    if (data) {
      const factory = classIdentifier.getFactory();
      for (let i = 0; i < data.length; i++) {
        try {
          const o = factory.make(data[i]);
          await this.putEntity(o, factory); // Update in db.
          if (cache.polled) {
            await observable.next(o); // NOTE: We only notify subscribers after polling is complete.
          }
        } catch (x) {
          console.log('Error parsing ' + classIdentifier.name + ' from poll.', x, data);
        }
      }
      if (data.length > 0 && cache.polled) {
        await observable.next(new SubscriberSentinel());
      }
    }
  }

  /**
   * After user + organization polls, ensure every product template referenced by the user's in-scope
   * products exists in IndexedDB. If not, clear the org poll cursor (etag), reset org poll completion
   * state, and Signin the organization worker with empty etag so the full 206 replay repopulates IndexedDB.
   */
  async ensurePrivateLoginProductTemplatesInCache(
    organizationId: string,
    countryCode: CountryCode,
    userId: string,
  ): Promise<void> {
    if (!this.organizationCache || this.organizationCache.organizationId !== organizationId) {
      throw new Error(`ensurePrivateLoginProductTemplatesInCache: organization poll not active for ${organizationId}.`);
    }

    const missing = await this.hasMissingProductTemplates(userId, organizationId, countryCode);

    if (!missing) {
      return;
    }

    this.organizationCache.resetInitialPollTracking();

    try {
      this.organizationPoll.postMessage({
        type: OrganizationPollRequestType.Signin,
        apiUrl: this.apiUrl,
        acceptHeader: this.acceptHeader,
        organizationId,
        etag: '',
      });

      await this.waitUntilOrganizationActiveAndPolled(organizationId);
    } catch (error) {
      console.error('Error ensuring private login product templates in cache:', error);
      throw error;
    }
  }

  private async hasMissingProductTemplates(
    userId: string,
    organizationId: string,
    countryCode: CountryCode,
  ): Promise<boolean> {
    const products = await this.getProductsByUserFromDb(userId);
    const relevant = products.filter((p) =>
      this.isProductInScopeForPrivateLoginProductTemplates(p, organizationId, countryCode),
    );
    const templateIds = [...new Set(relevant.map((p) => p.templateId).filter((id) => Boolean(id)))];

    for (const templateId of templateIds) {
      const t = await this.getEntityFromCache(templateId, ProductTemplate.getFactory());
      if (!t) {
        return true;
      }
    }
    return false;
  }

  /** Same product scope as dashboard `shouldIncludeProduct` (private portal, one market org). */
  private isProductInScopeForPrivateLoginProductTemplates(
    p: Product,
    organizationId: string,
    countryCode: CountryCode,
  ): boolean {
    if (!p.issuingOrganizationIds?.some((id) => id === organizationId)) {
      return false;
    }
    if (p.countryCode !== countryCode) {
      return false;
    }
    return true;
  }

  // Not used - Leftover from copy paste BO code
  async batchParsePollData<T>(
    cache: UserCache | OrganizationCache | CustomerOrganizationCache,
    data: T[],
    classIdentifier: any,
    observable: AwaitingSubject<T>,
  ) {
    const db = this.db;
    if (!db) {
      throw new Error('DB not initialized.');
    }

    if (!data) {
      return;
    }

    const BATCH_SIZE: number = 10000;

    const batches: number = Math.ceil(data.length / BATCH_SIZE);

    const factory = classIdentifier.getFactory();
    const table = factory.getTableName();

    let currentIndex = 0;

    for (let currentBatch = 1; currentBatch <= batches; currentBatch++) {
      await new Promise((resolve, reject) => {
        const transaction = db.transaction([table], 'readwrite');
        const store = transaction.objectStore(table);

        let batchEnd = currentBatch * BATCH_SIZE - 1;
        batchEnd = batchEnd > data.length ? data.length : batchEnd;

        while (currentIndex < batchEnd) {
          store.put(data[currentIndex]);
          if (observable.subCount > 0) {
            const o = factory.make(data[currentIndex]);
            observable.next(o);
          }
          currentIndex++;
        }

        transaction.oncomplete = (event) => {
          resolve(event);
        };

        transaction.onerror = (event) => {
          reject(event);
        };

        transaction.commit();
      });
    }

    if (data.length > 0 && cache.polled) {
      await observable.next(new SubscriberSentinel());
    }
  }

  async userHasPolled(): Promise<void> {
    if (!this.cache) {
      throw new Error('Checking if User has polled without being signed in.');
    }
    await this.isReady();
    await this.cache.hasPolled();
  }

  async forceUserPoll(): Promise<void> {
    if (!this.cache) {
      throw new Error('Cannot force User poll without being signed in.');
    }

    await this.isReady();
    this.cache.polled = false;
    this.userPoll.postMessage({
      type: UserPollRequestType.ForcePoll,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: this.cache.accessToken,
      refreshToken: this.cache.refreshToken,
      userId: this.cache.userId,
    });
  }

  async organizationHasPolled(): Promise<void> {
    if (!this.organizationCache) {
      throw new Error("Can't await Organization poll without having chosen a Organization.");
    }
    await this.isReady();
    await this.organizationCache.hasPolled();
  }

  /**
   * Waits until the given organization is the active organization and its poll has completed.
   * Use this in my-pages routes (e.g. dashboard) so that product template and other
   * org-scoped requests run only after the correct org context is ready (avoids 405 when
   * entering via a different country URL and then opening a business with another parent org).
   */
  async waitUntilOrganizationActiveAndPolled(organizationId: string): Promise<void> {
    await this.isReady();
    const self = this;
    const TIMEOUT_MS = 60 * 1000; // 1 minute max wait to avoid infinite loop on network failure / invalid ID
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(
          new Error(
            `waitUntilOrganizationActiveAndPolled: organization ${organizationId} did not become active and polled within ${TIMEOUT_MS}ms`,
          ),
        );
      }, TIMEOUT_MS);

      const check = function (): void {
        if (settled) return;
        if (self.organizationCache?.organizationId === organizationId) {
          self.organizationCache.hasPolled().then(() => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve();
          });
          return;
        }
        setTimeout(check, 50);
      };
      check();
    });
  }

  async customerOrganizationHasPolled(): Promise<void> {
    if (!this.customerOrganizationCache) {
      throw new Error("Can't await Customer Organization poll without having chosen a Customer Organization.");
    }
    await this.isReady();
    await this.customerOrganizationCache.hasPolled();
  }

  /**
   * Force an immediate customer organization poll. Resolves when the poll response has been
   * processed (organization and payment methods etc. updated in cache). Use after e.g. adding
   * a card so the UI can show real data from the backend.
   */
  async forceCustomerOrganizationPoll(): Promise<void> {
    if (!this.customerOrganizationCache) {
      throw new Error("Can't force Customer Organization poll without having chosen a Customer Organization.");
    }
    await this.isReady();
    return new Promise<void>((resolve) => {
      this.forceCustomerOrganizationPollResolve = resolve;
      this.customerOrganizationPoll.postMessage({
        type: CustomerOrganizationPollRequestType.ForcePoll,
      });
    });
  }

  //#endregion

  //#region Persistance

  public async putEntity<T>(entity: T, factory: Factory<T>): Promise<void> {
    await this.isReady();
    await new Promise<any>((resolve, reject) => {
      if (!this.db) {
        throw new Error('DB not initialized.');
      }
      try {
        const table = factory.getTableName();
        const os = this.db.transaction([table], 'readwrite').objectStore(table);
        const req = os.put(entity);

        req.onsuccess = (event) => {
          resolve(event);
        };

        req.onerror = (event) => {
          console.error('Error deleting database.', event);
          reject(event);
        };
      } catch (err) {
        console.log('Error saving entity in DB.', err);
        reject(err);
      }
    });
  }

  //#endregion

  //#region Communications

  private async send(
    method: string,
    endpoint: string,
    body?: object,
    cache?: UserCache,
    cursor?: string,
    refresh: boolean = true,
  ): Promise<object> {
    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      if (!body) {
        throw new Error('Body can not be undefined for POST, PUT or PATCH requests.');
      }
    } else if (body && method !== 'DELETE') {
      throw new Error('Only POST, PUT, PATCH and DELETE requests can have a body.');
    }

    const options: any = {
      headers: {
        Accept: this.acceptHeader,
      },
    };

    if (cache) {
      options['headers']['Authorization'] = 'Bearer ' + cache.accessToken;
    }

    if (method === 'PUT') {
      options['Content-Type'] = 'multipart/form-data;';
    }

    try {
      if (method === 'GET') {
        return await firstValueFrom(this.http.get(this.apiUrl + endpoint, options));
      } else if (method === 'POST') {
        return await firstValueFrom(this.http.post(this.apiUrl + endpoint, body, options));
      } else if (method === 'PUT') {
        return await firstValueFrom(this.http.put(this.apiUrl + endpoint, body, options));
      } else if (method === 'PATCH') {
        return await firstValueFrom(this.http.patch(this.apiUrl + endpoint, body, options));
      } else if (method === 'DELETE') {
        const deleteOptions = body ? { ...options, body } : options;
        return await firstValueFrom(this.http.request<object>(method, this.apiUrl + endpoint, deleteOptions));
      } else {
        throw new Error('Unrecognized method type.');
      }
    } catch (err: any) {
      if (err && err.status == 401 && cache !== null && refresh === true) {
        try {
          if (!cache) {
            throw new Error('Cache was uninitialized when refreshing token.');
          }
          await this.refreshToken();
          return this.send(method, endpoint, body, cache, cursor, false);
        } catch (x) {
          if (x) {
            const f = err.error?.fault;

            if (f) {
              // Is this a fault?
              throw new Fault(f);
            } else {
              throw err;
            }
          }
        }
      } else {
        const f = err.error?.fault;
        if (f) {
          // Is this a fault?
          throw new Fault(f);
        } else {
          throw err;
        }
      }
    }
    throw new Error('Unrecognized method type.');
  }

  //#endregion

  //#region Active

  getActiveUserId(): string | undefined {
    const c = this.cache;
    if (!c) {
      return undefined;
    }
    return c.userId;
  }

  async getActiveCustomerOrganizationId(): Promise<string | undefined> {
    const c = this.customerOrganizationCache;
    if (!c) {
      return undefined;
    }
    return c.customerOrganizationId;
  }

  async getActiveCustomerOrganization(): Promise<Organization | undefined> {
    const c = this.customerOrganizationCache;
    if (!c) {
      return undefined;
    }
    return c.customerOrganization;
  }

  async getActiveOrganizationId(): Promise<string | undefined> {
    const c = this.organizationCache;
    if (!c) {
      return undefined;
    }
    return c.organizationId;
  }

  async getActiveOrganization(): Promise<Organization | undefined> {
    const c = this.organizationCache;
    if (!c) {
      return undefined;
    }
    return c.organization;
  }

  async getActiveUser(): Promise<User | undefined> {
    const c = this.cache;
    if (!c) {
      return undefined;
    }

    if (c.user) {
      return c.user;
    } else {
      const u = await this.getUser(c.userId);
      c.setUser(u); // Refresh cache.
      return u;
    }
  }

  /**
   * Clear the in-memory cached User so the next `getActiveUser()` call
   * fetches a fresh copy from the API. Useful after server-side role
   * changes (e.g. ORGANIZATION_ADMIN added during org creation).
   */
  invalidateCachedUser(): void {
    if (this.cache) {
      this.cache.user = undefined;
    }
  }

  async setActiveUser(accessToken: string, refreshToken: string, userId: string) {
    // Persist.
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('userId', userId);

    const cache = new UserCache(accessToken, refreshToken, userId);

    this.cache = cache;

    this.userPoll.postMessage({
      type: UserPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken,
      userId: cache.userId,
      etag: localStorage.getItem('userCursor' + cache.userId) ?? '',
    });
  }

  async setActiveOrganization(organizationId: string) {
    if (this.organizationCache?.organizationId === organizationId) {
      return;
    }

    localStorage.setItem('organizationId', organizationId);

    this.organizationConfig.setActiveOrganization(organizationId);

    const organizationCache = new OrganizationCache(organizationId);

    // Start Organization poll.
    this.organizationPoll.postMessage({
      type: OrganizationPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      organizationId: organizationId,
      etag: localStorage.getItem('organizationCursor' + organizationId) ?? '',
    });

    this.organizationCache = organizationCache;
  }

  async setActiveCustomerOrganization(organizationId: string) {
    if (this.customerOrganizationCache?.customerOrganizationId === organizationId) {
      return;
    }

    localStorage.setItem('customerOrganizationId', organizationId);

    const customerOrganizationCache = new CustomerOrganizationCache(organizationId);

    const accessToken = this.cache?.accessToken || localStorage.getItem('accessToken');
    if (!accessToken) {
      throw new Error('Access token is required to start customer organization polling');
    }

    this.customerOrganizationPoll.postMessage({
      type: CustomerOrganizationPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: accessToken,
      customerOrganizationId: organizationId,
      etag: localStorage.getItem('customerOrganizationCursor' + organizationId) ?? '',
    });

    this.customerOrganizationCache = customerOrganizationCache;
  }

  getActiveCountryCode(): CountryCode | undefined {
    const code = localStorage.getItem('countryCode');
    if (code) {
      return code as CountryCode;
    }
    return undefined;
  }

  async setActiveCountryCode(code: CountryCode) {
    localStorage.setItem('countryCode', code);
  }
  //#endregion

  //#region Sign Up/In/Out

  async claimPhone(phone: string, language: LanguageCode) {
    if (!phone) {
      throw new Error('Trying to log in without phone.');
    }

    const body = { data: phone, extra: { language: language } };

    await this.send('POST', '/users/claim/phone', body);
    // TODO: Error handling??
  }

  async claimSpar(nid: string, extra: ExtraPhoneVerification) {
    if (!nid) {
      throw new Error('Trying to claim spar without nid.');
    }

    if (!extra) {
      throw new Error('Cant claim spar without extra information');
    }

    const requestPayload = {
      data: nid,
      extra: extra,
    };

    const res = (await this.send('POST', '/users/lookup/spar', requestPayload)) as DataResponse;

    if (!res.data) {
      throw new Error('Could not claim spar');
    }

    return res.data;
  }

  async signInByPhone(phone: string, verificationCode: string) {
    if (!phone) {
      throw new Error('Trying to log in without phone.');
    }

    if (!verificationCode) {
      throw new Error('Trying to log in without verificationCode.');
    }

    const res = (await this.send('POST', '/users/signin/phone', {
      data: phone,
      extra: { phoneVerificationCode: verificationCode },
    })) as SigninResponse;

    if (!res.data) {
      throw new Error('Response contained no data when logging in by phone.');
    }

    const cache = new UserCache(res.data.accessToken, res.data.refreshToken, res.data.userId);

    // Persist.
    localStorage.setItem('accessToken', cache.accessToken);
    localStorage.setItem('refreshToken', cache.refreshToken);
    localStorage.setItem('userId', cache.userId);

    this.cache = cache;

    this.userPoll.postMessage({
      type: UserPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken,
      userId: cache.userId,
      etag: localStorage.getItem('userCursor' + cache.userId) ?? '',
    });
  }

  async editUserPhone(phone: string, code: string): Promise<User> {
    if (!phone) {
      throw new Error('Trying to edit phone without phone');
    }
    if (!code) {
      throw new Error('Trying to edit phone without code');
    }

    const body = {
      data: phone,
      extra: { phoneVerificationCode: code },
    };

    const res = (await this.send('PUT', '/users/phone', body, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Response contained no data when editing phone');
    }

    const factory = User.getFactory();
    const user = factory.make(res.data);
    this.putEntity(user, factory);

    return user;
  }

  async verifyPhone(phone: string, code: string): Promise<boolean> {
    if (!phone) {
      throw new Error('Trying to verify phone without phone');
    }
    if (!code) {
      throw new Error('Trying to verify code without code');
    }

    const requestPayload = {
      data: phone,
      extra: code,
    };

    const res = await this.send('POST', '/users/claim/phone/verify', requestPayload);

    if ('data' in res && res.data === true) {
      return true;
    } else {
      throw new Error('Code did not match entered phonenumber');
    }
  }

  async sendVerificationEmail(organizationId: OrganizationEnum, user: User): Promise<DataResponse> {
    if (!user) {
      throw new Error('Trying to send verification email without user.');
    }

    const res = (await this.send(
      'POST',
      '/organizations/' + organizationId + '/users/' + user.id + '/send_email_verification',
      {},
      this.cache,
    )) as DataResponse;

    return res;
  }

  async verifyEmailByToken(token: string): Promise<DataResponse> {
    if (!token) {
      throw new Error('Trying to verify email without token.');
    }

    const body = {
      data: { token: token },
    };

    const res = (await this.send('POST', '/users/verify_email', body, undefined)) as DataResponse;

    return res;
  }

  async verifyCompanyEmailByToken(token: string): Promise<void> {
    if (!token) {
      throw new Error('Trying to verify company email without token.');
    }

    const body = {
      data: { token: token },
    };

    await this.send('POST', '/organizations/verify_company_email', body, undefined);
  }

  async claimEmail(email: string, language: LanguageCode, onlyClaimIfExisting: boolean = true): Promise<boolean> {
    if (!email) {
      throw new Error('Trying to claim email without email.');
    }

    const normalizedEmail = email.trim().toLowerCase();
    const body = { data: normalizedEmail, extra: { onlyClaimIfExisting: onlyClaimIfExisting, language: language } };

    const res = (await this.send('POST', '/users/claim/email', body)) as DataResponse;

    return Boolean(res.data);
  }

  async claimEmailVerify(email: string, code: string): Promise<boolean> {
    if (!email) {
      throw new Error('Trying to verify claim email without email.');
    }
    if (!code) {
      throw new Error('Trying to verify claim email without code.');
    }

    const normalizedEmail = email.trim().toLowerCase();
    const trimmedCode = code.trim();

    const body = { data: normalizedEmail, extra: trimmedCode };

    const res = (await this.send('POST', '/users/claim/email/verify', body)) as DataResponse;

    return Boolean(res.data);
  }

  async sendCompanyEmailVerification(organizationId: string): Promise<void> {
    await this.send('POST', `/organizations/${organizationId}/send_company_email_verification`, {}, this.cache);
  }

  async signInByEmail(email: string, verificationCode: string) {
    if (!email) {
      throw new Error('Trying to log in without email.');
    }

    if (!verificationCode) {
      throw new Error('Trying to log in without verificationCode.');
    }

    const res = (await this.send('POST', '/users/signin/email', {
      data: email,
      extra: { emailVerificationCode: verificationCode },
    })) as SigninResponse;

    if (!res.data) {
      throw new Error('Response contained no data when logging in by email.');
    }

    const cache = new UserCache(res.data.accessToken, res.data.refreshToken, res.data.userId);

    // Persist.
    localStorage.setItem('accessToken', cache.accessToken);
    localStorage.setItem('refreshToken', cache.refreshToken);
    localStorage.setItem('userId', cache.userId);

    this.cache = cache;

    this.userPoll.postMessage({
      type: UserPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken,
      userId: cache.userId,
      etag: localStorage.getItem('userCursor' + cache.userId) ?? '',
    });
  }

  async signOut() {
    this.userPoll.postMessage({ type: UserPollRequestType.Signout });
    // this.countryPoll.postMessage({ type: CountryPollRequestType.Signout });

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('countryCode');

    this.cache = undefined;
  }

  async conditionalSignOut() {
    if (this.cache) {
      await this.signOut();
    }
  }

  async resetCache() {
    this.userPoll.postMessage({ type: UserPollRequestType.Signout });
    // this.countryPoll.postMessage({ type: CountryPollRequestType.Signout });

    localStorage.clear();

    await new Promise<any>((resolve, reject) => {
      const req: IDBOpenDBRequest = indexedDB.deleteDatabase('VikingWebShopDB');

      req.onblocked = (event) => {
        console.log("Couldn't delete database due to the operation being blocked.", event);
        this.db?.close();
      };

      req.onerror = (event) => {
        console.error('Error deleting database.', event);
        reject(event);
      };

      req.onsuccess = (event) => {
        console.log('Database deleted successfully.', event);
        resolve(event); // event.result should be undefined on success
      };
    });

    this.cache = undefined;
  }

  async refreshToken() {
    if (!this.cache) {
      throw new Error('Cache service must be initialized before token refresh.');
    }

    const body = { data: this.cache.refreshToken };

    const res = (await this.send(
      'POST',
      '/users/' + this.cache.userId + '/token/refresh',
      body,
      this.cache,
    )) as DataResponse;

    if (!res.data) {
      throw new Error('Token refresh response has no data.');
    }

    this.cache.accessToken = res.data;
    localStorage.setItem('accessToken', this.cache.accessToken); // Persist.
    if (this.customerOrganizationCache) {
      this.customerOrganizationPoll.postMessage({
        type: CustomerOrganizationPollRequestType.TokenRefresh,
        accessToken: this.cache.accessToken,
      });
    }
  }

  //#endregion

  //#region Subscription

  private async subscribe<T extends Deletable>(
    factory: Factory<T>,
    observable: AwaitingSubject<T>,
    next: (value: T | SubscriberSentinel) => Promise<void>,
    deleted: boolean = true,
    provideAll: boolean = true,
  ): Promise<Subscription<T>> {
    // Get active user.
    const c = this.cache;
    if (!c) {
      throw new Error('Cannot subscribe without being signed in.');
    }

    await this.isReady();

    const s = observable.subscribe({
      next: async (c) => {
        if (c instanceof SubscriberSentinel || deleted || !c.deleted) {
          await next(c);
        }
      },
    });

    // Provide all in db.
    if (provideAll) {
      const entities = await this.getAllEntitiesFromDb(factory);
      for (let i = 0; i < entities.length; i++) {
        if (deleted || !entities[i].deleted) {
          await next(entities[i]);
        }
      }
    }

    await next(new SubscriberSentinel()); // Send signal that all available have been sent.

    return s;
  }

  private async subscribeByCountry<T extends HasCountryCode>(
    countryCode: CountryCode,
    factory: Factory<T>,
    observable: AwaitingSubject<T>,
    next: (value: T | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<T>> {
    const c = this.organizationCache;
    if (!c) {
      throw new Error('Cannot subscribe without being signed in organization.');
    }

    await this.isReady();

    const sub = observable.subscribe({
      next: async (o) => {
        if (o instanceof SubscriberSentinel || o.countryCode === countryCode) {
          await next(o);
        }
      },
    });

    // Provide all in DB.
    const entities = await this.getEntitiesFromDbByIndex(countryCode, 'countryCode', factory);
    for (let i = 0; i < entities.length; i++) {
      const e: any = entities[i];
      if (e.countryCode === countryCode && !e.deleted) {
        await next(e);
      }
    }

    // Send signal that all available have been sent.
    await next(new SubscriberSentinel());

    return sub;
  }

  public async subscribeToUsers(
    next: (value: User | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true,
  ): Promise<Subscription<User>> {
    return this.subscribe(User.getFactory(), this.users$, next, true, provideAll);
  }

  public async subscribeToStories(
    countryCode: CountryCode,
    next: (value: Story | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<Story>> {
    return this.subscribeByCountry(countryCode, Story.getFactory(), this.stories$, next);
  }

  public async subscribeToVehicleBrands(
    next: (value: VehicleBrand | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<VehicleBrand>> {
    return this.subscribe(VehicleBrand.getFactory(), this.vehicleBrands$, next);
  }

  public async subscribeToVehicleModels(
    next: (value: VehicleModel | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<VehicleModel>> {
    return this.subscribe(VehicleModel.getFactory(), this.vehicleModels$, next);
  }

  public async subscribeToProductTemplates(
    countryCode: CountryCode,
    next: (value: ProductTemplate | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<ProductTemplate>> {
    return this.subscribeByCountry(countryCode, ProductTemplate.getFactory(), this.productTemplates$, next);
  }

  public async subscribeToProducts(
    next: (value: Product | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<Product>> {
    return this.subscribe(Product.getFactory(), this.products$, next);
  }

  public async subscribeToPaymentMethods(
    next: (value: PaymentMethod | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<PaymentMethod>> {
    return this.subscribe(PaymentMethod.getFactory(), this.paymentMethods$, next);
  }

  public async subscribeToObjects(
    next: (value: VikingObject | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<VikingObject>> {
    return this.subscribe(VikingObject.getFactory(), this.objects$, next);
  }

  public async subscribeToPayments(
    next: (value: Payment | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<Payment>> {
    return this.subscribe(Payment.getFactory(), this.payments$, async (value) => {
      if (!(value instanceof SubscriberSentinel) && value.isMothershipPayment()) {
        return;
      }
      await next(value);
    });
  }

  public async subscribeToDiscounts(
    next: (value: Discount | SubscriberSentinel) => Promise<void>,
  ): Promise<Subscription<Discount>> {
    return this.subscribe(Discount.getFactory(), this.discounts$, next);
  }

  //#endregion

  //#region Count
  //#endregion

  //#region Get

  public async getEntityFromApi<T>(url: string, factory: Factory<T>): Promise<T> {
    const res = (await this.send('GET', url, undefined, this.cache)) as any;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    return factory.make(res.data);
  }

  private async getEntity<T>(id: string, factory: Factory<T>, url: string, invalidate: boolean = false): Promise<T> {
    await this.isReady();

    // If old object is invalidated fetch latest from API right away.
    if (invalidate) {
      const e: T = await this.getEntityFromApi(url, factory);
      await this.putEntity(e, factory); // Update in db.
      return new Promise<T>((resolve) => resolve(e));
    }

    // Try to look in db.

    const self = this;
    return new Promise<T>(function (resolve, reject) {
      if (self.db == null) {
        throw new Error('DB was not initialized.');
      }

      // Check db.
      const table: string = factory.getTableName();
      const os = self.db.transaction([table], 'readonly').objectStore(table);
      const req = os.get(id);

      req.onerror = async function () {
        // Entity not found in db, fetch from server.
        try {
          const e: T = await self.getEntityFromApi(url, factory);
          self.putEntity(e, factory); // Update in db.
          resolve(e);
        } catch (x) {
          reject(x);
        }
      };

      req.onsuccess = async function () {
        // Entity found in db.
        try {
          let e: T;
          if (invalidate) {
            e = await self.getEntityFromApi(url, factory);
            self.putEntity(e, factory); // Update in db.
          } else {
            if (req.result) {
              e = factory.make(req.result);
            } else {
              e = await self.getEntityFromApi(url, factory);
              self.putEntity(e, factory); // Update in db.
            }
          }
          resolve(e);
        } catch (x) {
          reject(x);
        }
      };
    });
  }

  /**
   * Get a single entity from IndexedDB by id. No API call.
   * Use when the entity is already in cache (e.g. from poll) and the single-entity GET is restricted or unnecessary.
   * @returns The entity if found in cache, undefined otherwise.
   */
  public async getEntityFromCache<T>(id: string, factory: Factory<T>): Promise<T | undefined> {
    await this.isReady();
    if (!this.db) return undefined;
    const table = factory.getTableName();
    return new Promise<T | undefined>((resolve) => {
      const os = this.db!.transaction([table], 'readonly').objectStore(table);
      const req = os.get(id);
      req.onsuccess = () => resolve(req.result ? factory.make(req.result) : undefined);
      req.onerror = () => resolve(undefined);
    });
  }

  public async getAllEntitiesFromDb<T>(factory: Factory<T>): Promise<T[]> {
    await this.isReady();

    const self = this;

    const table: string = factory.getTableName();
    return new Promise<T[]>(function (resolve, reject) {
      const res: T[] = [];
      const os = self.db!.transaction([table], 'readonly').objectStore(table);
      const req = os.openCursor();
      req.onerror = async function (event: any) {
        reject(event);
      };
      req.onsuccess = function (_event) {
        const cursor = req.result;
        if (cursor) {
          const o = factory.make(cursor.value);
          res.push(o);
          cursor.continue();
        } else {
          resolve(res);
        }
      };
    });
  }

  public async getEntitiesFromDbByIndex<T>(range: any, index: string, factory: Factory<T>): Promise<T[]> {
    const self = this;
    const table = factory.getTableName();

    await this.isReady();
    return new Promise<T[]>(function (resolve, reject) {
      const res: T[] = [];
      const os = self.db!.transaction([table], 'readonly').objectStore(table);

      const req = (() => {
        const _range = IDBKeyRange.only(range);
        const indx = os.index(index);
        return indx.openCursor(_range);
      })();

      req.onerror = async function (event: any) {
        reject(event);
      };

      req.onsuccess = function (_event) {
        const cursor = req.result;
        if (cursor) {
          const o = factory.make(cursor.value);
          res.push(o);
          cursor.continue();
        } else {
          resolve(res);
        }
      };
    });
  }

  async getUser(id: string, invalidate: boolean = false): Promise<User> {
    return this.getEntity(id, User.getFactory(), User.getUrl(id), invalidate);
  }

  async getOrganization(id: string, invalidate: boolean = false): Promise<Organization> {
    return this.getEntity(id, Organization.getFactory(), Organization.getUrl(id), invalidate);
  }

  async patchOrganizationCompanyEmail(
    organizationId: string,
    companyEmail: { address: string } | null,
  ): Promise<Organization> {
    const body = {
      data: {},
      extra: {
        companyEmail: companyEmail ? { address: companyEmail.address.trim(), status: EmailStatus.Unverified } : null,
      },
    };
    const res = (await this.send(
      'PUT',
      `/organizations/${organizationId}/capabilities/customer/company_email`,
      body,
      this.cache,
    )) as { data?: Organization };

    if (!res.data) {
      throw new Error('Response contained no data when updating company email.');
    }
    const org = Organization.getFactory().make(res.data);
    await this.putEntity(org, Organization.getFactory());
    await this.organizations$.next(org);
    await this.organizations$.next(new SubscriberSentinel());
    return org;
  }

  async patchOrganizationInvoiceEmail(organizationId: string, invoiceEmail: string | null): Promise<Organization> {
    const body = {
      data: {},
      extra: {
        invoiceEmail: invoiceEmail ? invoiceEmail.trim() : null,
      },
    };
    const res = (await this.send(
      'PUT',
      `/organizations/${organizationId}/capabilities/customer/invoice_email`,
      body,
      this.cache,
    )) as { data?: Organization };

    if (!res.data) {
      throw new Error('Response contained no data when updating invoice email.');
    }
    const org = Organization.getFactory().make(res.data);
    await this.putEntity(org, Organization.getFactory());
    await this.organizations$.next(org);
    await this.organizations$.next(new SubscriberSentinel());
    return org;
  }

  async organizationCustomerLookup(id: string): Promise<Organization[]> {
    const res = (await this.send('GET', `/users/${id}/organization-customers`, undefined, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const organizations: Organization[] = [];
    const f = Organization.getFactory();
    for (let i = 0; i < res.data.length; i++) {
      const o: Organization = f.make(res.data[i]);
      organizations.push(o);
      await this.putEntity(o, f);
    }

    return organizations;
  }

  /**
   * Create a new customer organization (business).
   * Accepts a pre-built CustomerOrganizationCreate payload from the caller.
   */
  async createCustomerOrganization(
    customerOrganization: CustomerOrganizationCreate,
    adminUserId?: string,
  ): Promise<Organization> {
    const extra = adminUserId ? { adminUserId } : undefined;
    const body = { data: customerOrganization, extra };
    const res = (await this.send('POST', '/organizations/customer', body, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Response contained no data when creating customer organization.');
    }

    const factory = Organization.getFactory();
    const newOrganization = factory.make(res.data);
    await this.putEntity(newOrganization, factory);

    if (this.organizations$) {
      await this.organizations$.next(newOrganization);
      await this.organizations$.next(new SubscriberSentinel());
    }

    return newOrganization;
  }

  /**
   * Create a new user and associate with an organization.
   * Mirrors backend POST /users (users_post).
   */
  async createUserForOrganization(body: CreateUserRequest): Promise<NewUserResponse> {
    const res = (await this.send('POST', '/users', body, undefined)) as DataResponse;

    if (!res.data) {
      throw new Error('Response contained no data when creating user.');
    }
    return new NewUserResponse(res.data);
  }

  /**
   * Public lead form: POST /business-customer-requests (rate-limited, unauthenticated).
   */
  async submitBusinessCustomerRequest(payload: BusinessCustomerRequestPayload): Promise<boolean> {
    const res = (await this.send('POST', '/business-customer-requests', payload, undefined)) as DataResponse;
    return (res.data as { sent?: boolean } | undefined)?.sent === true;
  }

  /** @deprecated Use {@link submitBusinessCustomerRequest}. */
  async submitBusinessSignupRequest(payload: BusinessCustomerRequestPayload): Promise<void> {
    await this.submitBusinessCustomerRequest(payload);
  }

  async getVehicleBrand(id: string, invalidate: boolean = false): Promise<VehicleBrand> {
    return this.getEntity(id, VehicleBrand.getFactory(), VehicleBrand.getUrl(id), invalidate);
  }

  async getVehicleModel(id: string, invalidate: boolean = false): Promise<VehicleModel> {
    return this.getEntity(id, VehicleModel.getFactory(), VehicleModel.getUrl(id), invalidate);
  }

  async getVehicleLookup(countryCode: CountryCode, regNr: string): Promise<VehicleLookup> {
    const res = (await this.send(
      'GET',
      `/countries/${countryCode}/vehicles/lookup?licensePlate=${regNr}`,
      undefined,
      this.cache,
    )) as DataResponse;
    if (!res.data) {
      throw new Error('Data not found.');
    }

    return res.data;
  }

  /**
   * Get a product template from IndexedDB only. There is no single-template GET API; templates
   * are loaded by the product templates poll. Throws if not found in cache.
   */
  async getProductTemplate(
    _countryCode: CountryCode,
    id: string,
    _invalidate: boolean = false,
  ): Promise<ProductTemplate> {
    const template = await this.getEntityFromCache(id, ProductTemplate.getFactory());
    if (!template) {
      throw new Error(`Product template ${id} not in cache. Templates are loaded by the organization poll.`);
    }
    return template;
  }

  /**
   * Get a product from IndexedDB only. Single-product GET is not allowed for non-admins;
   * products are loaded by the products poll.
   */
  async getProduct(_countryCode: CountryCode, id: string, _invalidate: boolean = false): Promise<Product> {
    const product = await this.getEntityFromCache(id, Product.getFactory());
    if (!product) {
      throw new Error(`Product ${id} not in cache. Products are loaded by the products poll.`);
    }
    return product;
  }

  /**
   * Get a VikingObject from IndexedDB only. Single-object GET is not allowed for non-admins;
   * objects are loaded by the poll.
   */
  async getObject(_countryCode: CountryCode, id: string, _invalidate: boolean = false): Promise<VikingObject> {
    const obj = await this.getEntityFromCache(id, VikingObject.getFactory());
    if (!obj) {
      throw new Error(`Object ${id} not in cache. Objects are loaded by the poll.`);
    }
    return obj;
  }

  /**
   * Get a payment method from IndexedDB only. Single-payment-method GET is not allowed for non-admins;
   * payment methods are loaded by the user/org poll.
   */
  async getPaymentMethod(id: string): Promise<PaymentMethod> {
    const pm = await this.getEntityFromCache(id, PaymentMethod.getFactory());
    if (!pm) {
      throw new Error(`Payment method ${id} not in cache. Payment methods are loaded by the poll.`);
    }
    return pm;
  }

  // async getDiscountTempalte(countryCode: CountryCode, id: string, invalidate: boolean = false): Promise<DiscountTemplate> {
  //   return this.getEntity(id, DiscountTemplate.getFactory(), DiscountTemplate.getUrl(countryCode, id), invalidate);
  // }

  async getProductsByUserFromDb(userId: string): Promise<Product[]> {
    let products = await this.getEntitiesFromDbByIndex(userId, 'userId', Product.getFactory());

    const all = await this.getAllEntitiesFromDb(Product.getFactory());

    if (products.length === 0) {
      products = all.filter((p: Product) => p.beneficiaryId === userId && !p.deleted);
    }

    const policyholderProducts = all.filter(
      (p: Product) => p.policyholder?.type === ProductOwnerRefType.User && p.policyholder.id === userId && !p.deleted,
    );

    const mergedById = new Map<string, Product>();
    for (const product of [...products, ...policyholderProducts]) {
      mergedById.set(product.id, product);
    }

    return Array.from(mergedById.values());
  }

  async getChildProductsFromDb(parentProductId: string): Promise<Product[]> {
    const all = await this.getAllEntitiesFromDb(Product.getFactory());
    return all.filter((product) => !product.deleted && product.parent?.id === parentProductId);
  }

  async getDiscount(_countryCode: CountryCode, id: string, _invalidate: boolean = false): Promise<Discount> {
    const discount = await this.getEntityFromCache(id, Discount.getFactory());
    if (!discount) {
      throw new Error(`Discount ${id} not in cache.`);
    }
    return discount;
  }

  async getVehicle(countryCode: CountryCode, vehicleId: string): Promise<Vehicle> {
    const o = await this.getEntity(
      vehicleId,
      VikingObject.getFactory(),
      VikingObject.getUrl(countryCode, vehicleId),
      false,
    );

    if (o.type === VikingObjectType.Vehicle) {
      return o as Vehicle;
    }

    throw new Error('Requested Object ' + vehicleId + ' was not of type VehicleObject.');
  }

  async getInterest(countryCode: CountryCode, interestId: string): Promise<Interest> {
    const o = await this.getEntity(
      interestId,
      VikingObject.getFactory(),
      VikingObject.getUrl(countryCode, interestId),
      false,
    );

    if (o.type === VikingObjectType.Interest) {
      return o as Interest;
    }

    throw new Error('Requested Object ' + interestId + ' was not of type Interest.');
  }

  async productBeneficiaryLookUp(
    countryCode: CountryCode,
    phonenumber: string,
  ): Promise<{ type: User; userId: string; firstName: string; lastName: string } | undefined> {
    await this.isReady();

    const url = `/countries/${countryCode}/products/beneficiary/lookup?phone_number=${phonenumber}`;
    const res = (await this.send('GET', url, undefined, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    return res.data;
  }

  /**
   * Look up a user by email via the product beneficiary lookup API.
   * Returns minimal user info (userId, firstName, lastName). Use getUser(userId) for full User.
   */
  async productBeneficiaryLookupByEmail(countryCode: CountryCode, email: string): Promise<LookupUserResultDto> {
    await this.isReady();

    const url = `/countries/${countryCode}/products/beneficiary/lookup?email=${encodeURIComponent(email)}`;
    const res = (await this.send('GET', url, undefined, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    return res.data;
  }

  public async addOrganizationAdmin(userId: string, organizationId: string): Promise<User> {
    await this.isReady();
    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot add an organization admin without being signed in.');
    }

    const body = {
      data: {
        userId: userId,
      },
    };

    const res = (await this.send('POST', `/organizations/${organizationId}/users`, body, cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = User.getFactory();
    const admin = factory.make(res.data);
    await this.putEntity(admin, factory);

    return admin;
  }

  public async removeOrganizationAdmin(organizationId: string, userId: string): Promise<User> {
    await this.isReady();
    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot remove an organization admin without being signed in.');
    }
    const body = { data: { userId: userId } };
    const res = (await this.send('DELETE', `/organizations/${organizationId}/users`, body, cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = User.getFactory();
    const admin = factory.make(res.data);
    await this.putEntity(admin, factory);

    return admin;
  }

  public async addInterest(i: Interest): Promise<Interest> {
    if (!i) {
      throw new Error('Object must be present.');
    }
    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot add an object without being signed in.');
    }

    const res = (await this.send('POST', Interest.getUrl(i.countryCode), { data: i }, cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = Interest.getFactory();
    const obj = factory.make(res.data);
    await this.putEntity(obj, factory); // Update in db.

    if (obj instanceof Interest) {
      return obj;
    }

    throw new Error('Unexpected object type when adding interest.');
  }

  public async deleteInterest(countryCode: CountryCode, objectId: string): Promise<Interest> {
    if (!countryCode || !objectId) {
      throw new Error('Country code and object ID must be present.');
    }

    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot delete an object without being signed in.');
    }

    const res = (await this.send(
      'DELETE',
      VikingObject.getUrl(countryCode, objectId),
      undefined,
      cache,
    )) as DataResponse; // Endret body til null
    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = Interest.getFactory();
    const obj = factory.make(res.data);
    await this.putEntity(obj, factory);

    if (obj instanceof Interest) {
      return obj;
    }

    throw new Error('Unexpected object type when deleting interest.');
  }

  public async addVehicleObject(v: Vehicle, extra?: AddVehicleExtraRequest): Promise<Vehicle> {
    if (!v) {
      throw new Error('Object must be present.');
    }

    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot add an object without being signed in.');
    }

    const body = extra ? { data: v, extra } : { data: v };
    const res = (await this.send('POST', Vehicle.getUrl(v.countryCode), body, cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = Vehicle.getFactory();
    const newVehicle = factory.make(res.data) as Vehicle;
    await this.putEntity(newVehicle, factory); // Update in db.

    // Persist the auto-created vehicle model returned by the backend (if any).
    if (res.extra?.vehicleModel) {
      const modelFactory = VehicleModel.getFactory();
      const newModel = modelFactory.make(res.extra.vehicleModel);
      await this.putEntity(newModel, modelFactory);
    }

    return newVehicle;
  }

  /**
   * Validates a discount code for an organization. Returns the discount template when valid.
   * @param coversType - Optional. Pass 'Organization' when validating a business discount (e.g. capabilities.customer.discount).
   */
  async validateDiscount(
    countryCode: CountryCode,
    code: string,
    organizationId: string,
    coversType?: DiscountEligibilityType,
  ): Promise<DiscountTemplate> {
    await this.isReady();

    const data: { code: string; organizationId: string; coversType?: DiscountEligibilityType } = {
      code,
      organizationId,
    };
    if (coversType) {
      data.coversType = coversType;
    }

    const body = { data };

    const res = (await this.send('POST', `/countries/${countryCode}/discounts/validate`, body, undefined)) as any;

    // If false, discount is invalid
    if (!res.data) {
      throw new Error('Discount is invalid');
    }

    // Discount template not found
    if (!res.extra) {
      throw new Error('Data not found.');
    }

    const factory = DiscountTemplate.getFactory();
    const discountTemplate = factory.make(res.extra);

    return discountTemplate;
  }

  async claimDiscount(countryCode: CountryCode, discountCode: string): Promise<Discount> {
    await this.isReady();
    const c = this.cache;
    if (!c) {
      throw new Error('Can not claim discount without being signed in');
    }

    const res = (await this.send(
      'GET',
      `/users/${c.userId}/discounts/claim/${countryCode}${discountCode}`,
      undefined,
      c,
    )) as any;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = Discount.getFactory();
    const dc: Discount = factory.make(res.data);
    await this.putEntity(dc, factory); // Update in db.

    return dc;
  }

  async submitPaymentByNewCard(
    product: Product,
    firstPaymentPrice: string,
    language: string,
    type: string,
  ): Promise<string> {
    if (!this.cache) {
      throw new Error('Can not request payment without being signed in');
    }

    product.source = ProductSource.MyPages;

    const body = {
      data: product,
      extra: {
        paymentMethod: {
          type: type, // ENUM this later.
          language: language, // no-NO || sv-SE
          makeDefaultPaymentMethod: true,
        },
        firstPaymentPrice: firstPaymentPrice, // Price after discount.
      },
    };

    const res = (await this.send('POST', `/countries/${product.countryCode}/products`, body, this.cache)) as any;

    return res.extra.token;
  }

  async submitPaymentByExistingCard(
    product: Product,
    firstPaymentPrice: string,
    language: string,
    type: string,
    trialVoucherId?: string,
  ): Promise<Product> {
    if (!this.cache) {
      throw new Error('Can not request payment without being signed in');
    }

    product.source = ProductSource.MyPages;

    let body;

    if (trialVoucherId) {
      body = {
        data: product,
        extra: {
          paymentMethod: {
            type: type, // ENUM this later.
            language: language, // no-NO || sv-SE
            makeDefaultPaymentMethod: true,
          },
          firstPaymentPrice: firstPaymentPrice, // Price after discount.
          trialOptions: {
            discountId: trialVoucherId,
          },
        },
      };
    } else {
      body = {
        data: product,
        extra: {
          paymentMethod: {
            type: type, // ENUM this later.
            language: language, // no-NO || sv-SE
            makeDefaultPaymentMethod: true,
          },
          firstPaymentPrice: firstPaymentPrice, // Price after discount.
        },
      };
    }

    const res = (await this.send(
      'POST',
      `/countries/${product.countryCode}/products`,
      body,
      this.cache,
    )) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = Product.getFactory();
    const p: Product = factory.make(res.data);

    if (res.extra && res.extra.payment) {
      const paymentFactory = Payment.getFactory();
      const payment = paymentFactory.make(res.extra.payment);
      await this.putEntity(payment, paymentFactory);
    }

    await this.putEntity(p, factory);

    return p;
  }

  async editProductStatus(
    countryCode: CountryCode,
    productId: string,
    newStatuts: ProductStatus,
    terminateAt?: Date,
    cancellationReason?: CancellationReason,
  ): Promise<Product> {
    const c = this.cache;
    if (!c) {
      throw new Error('Can not claim discount without being signed in');
    }

    const extra: { terminateAt?: Date; cancellationReason?: CancellationReason } = {};
    if (terminateAt) {
      extra.terminateAt = terminateAt;
    }
    if (cancellationReason) {
      extra.cancellationReason = cancellationReason;
    }

    const res = (await this.send(
      'PUT',
      `/countries/${countryCode}/products/${productId}/status`,
      { data: newStatuts, extra },
      c,
    )) as DataResponse;

    return this.persistProductMutation(res);
  }

  async getProductRegretPreview(countryCode: CountryCode, productId: string): Promise<ProductRegretPreview> {
    const c = this.cache;
    if (!c) {
      throw new Error('Can not get regret preview without being signed in');
    }

    const product = await this.getProduct(countryCode, productId);
    const res = (await this.send(
      'GET',
      Product.getRegretPreviewUrl(countryCode, productId),
      undefined,
      c,
    )) as DataResponse;

    return this.parseProductRegretPreview(res, product.currency);
  }

  async regretProduct(
    countryCode: CountryCode,
    productId: string,
    cancellationReason?: CancellationReason,
  ): Promise<Product> {
    const c = this.cache;
    if (!c) {
      throw new Error('Can not regret product without being signed in');
    }

    const extra = cancellationReason ? { cancellationReason } : {};
    const res = (await this.send(
      'POST',
      Product.getRegretUrl(countryCode, productId),
      { extra },
      c,
    )) as DataResponse;

    return this.persistProductMutation(res);
  }

  private async persistProductMutation(res: DataResponse): Promise<Product> {
    const factory = Product.getFactory();
    const product: Product = factory.make(res.data);
    await this.putEntity(product, factory);

    if (res?.extra?.affectedProducts) {
      const affectedProducts = res.extra.affectedProducts as unknown[];
      for (const affected of affectedProducts) {
        const child: Product = factory.make(affected);
        await this.putEntity(child, factory);
      }
    }

    if (res?.extra?.affectedPayments) {
      const paymentFactory = Payment.getFactory();
      const affectedPayments = res.extra.affectedPayments;

      for (let i = 0; i < affectedPayments.length; i++) {
        const payment: Payment = paymentFactory.make(affectedPayments[i]);
        await this.putEntity(payment, paymentFactory);
      }
    }

    return product;
  }

  private parseProductRegretPreview(res: DataResponse, fallbackCurrency: Currency): ProductRegretPreview {
    const raw = (res?.data ?? {}) as Record<string, unknown>;
    const affectedChildProductIds = Array.isArray(raw['affectedChildProductIds'])
      ? (raw['affectedChildProductIds'] as unknown[]).filter((id): id is string => typeof id === 'string')
      : [];
    const products = Array.isArray(raw['products'])
      ? raw['products']
          .filter((product): product is Record<string, unknown> => typeof product === 'object' && product !== null)
          .map((product) => this.parseProductRegretSettlement(product))
      : [];

    return {
      refundAmount: this.toFiniteNumber(raw['refundAmount']),
      usedAmount: this.toFiniteNumber(raw['usedAmount']),
      creditAmount: this.toFiniteNumber(raw['creditAmount']),
      moneyRefundAmount: this.toFiniteNumber(raw['moneyRefundAmount']),
      unpaidCreditAmount: this.toFiniteNumber(raw['unpaidCreditAmount']),
      settlementType: typeof raw['settlementType'] === 'string' ? raw['settlementType'] : '',
      withinQualifyingPeriod: Boolean(raw['withinQualifyingPeriod']),
      createsNewInvoice: Boolean(raw['createsNewInvoice']),
      newInvoiceAmount: this.toFiniteNumber(raw['newInvoiceAmount']),
      currency: (raw['currency'] as Currency | undefined) ?? fallbackCurrency,
      affectedChildProductIds,
      products,
      regretDaysRemaining: this.toFiniteNumber(raw['regretDaysRemaining']),
    };
  }

  private parseProductRegretSettlement(raw: Record<string, unknown>): ProductRegretSettlement {
    return {
      productId: typeof raw['productId'] === 'string' ? raw['productId'] : '',
      paymentId: typeof raw['paymentId'] === 'string' ? raw['paymentId'] : undefined,
      settlementType: typeof raw['settlementType'] === 'string' ? raw['settlementType'] : '',
      refundAmount: this.toFiniteNumber(raw['refundAmount']),
      creditAmount: this.toFiniteNumber(raw['creditAmount']),
      moneyRefundAmount: this.toFiniteNumber(raw['moneyRefundAmount']),
      unpaidCreditAmount: this.toFiniteNumber(raw['unpaidCreditAmount']),
      usedAmount: this.toFiniteNumber(raw['usedAmount']),
      alreadyPaid: Boolean(raw['alreadyPaid']),
    };
  }

  private toFiniteNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  async addOrganizationProduct(productData: OrganizationProductData): Promise<Product> {
    if (!this.cache) {
      throw new Error('Can not request payment without being signed in');
    }

    const body = {
      templateId: productData.templateId,
      beneficiary: productData.beneficiary,
      start: productData.start,
      period: productData.period,
      paymentSchedule: productData.paymentSchedule,
      source: ProductSource.MyPages,
      ...(productData.discountCode ? { discountCode: productData.discountCode } : {}),
    };
    const res = (await this.send(
      'POST',
      `/organizations/${this.customerOrganizationCache?.customerOrganizationId}/products`,
      { data: body },
      this.cache,
    )) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = Product.getFactory();
    const addedProduct = factory.make(res.data);
    await this.putEntity(addedProduct, factory); // Update in db.
    await this.products$.next(addedProduct);
    await this.products$.next(new SubscriberSentinel());

    if (res.extra && res.extra.payment) {
      const paymentFactory = Payment.getFactory();
      const payment = paymentFactory.make(res.extra.payment);
      await this.putEntity(payment, paymentFactory);
      await this.payments$.next(payment);
      await this.payments$.next(new SubscriberSentinel());
    }

    if (res.extra && res.extra.discount) {
      const discountFactory = Discount.getFactory();
      const discount = discountFactory.make(res.extra.discount);
      await this.putEntity(discount, discountFactory);
      await this.discounts$.next(discount);
      await this.discounts$.next(new SubscriberSentinel());
    }

    if (res.extra && res.extra.vehicle) {
      const vehicleFactory = Vehicle.getFactory();
      const vehicle = vehicleFactory.make(res.extra.vehicle);
      if (vehicle instanceof Vehicle) {
        // Backend may return the vehicle as it was before the purchase, so make sure the ref is there.
        this.addProductRefToVehicle(vehicle, addedProduct);
      }
      await this.putEntity(vehicle, vehicleFactory);
      await this.objects$.next(vehicle);
      await this.objects$.next(new SubscriberSentinel());
    } else {
      // If backend did not return the updated vehicle; update vehicle in cache so UI stays in sync.
      await this.addProductRefToVehicleInCache(productData.beneficiary.objectId, addedProduct);
    }

    return addedProduct;
  }

  /** Adds the product ref to the vehicle when missing, so the vehicle list sees the product immediately. */
  private addProductRefToVehicle(vehicle: Vehicle, product: Product): void {
    if (vehicle.products?.some((vp) => vp.id === product.id)) return;

    const newRef = new VehicleProduct({ id: product.id, countryCode: product.countryCode });
    vehicle.products = [...(vehicle.products ?? []), newRef];
  }

  /**
   * When backend does not return the updated vehicle, add the product ref to the vehicle in cache and notify.
   * Used only from addOrganizationProduct.
   */
  private async addProductRefToVehicleInCache(vehicleId: string, product: Product): Promise<void> {
    try {
      const obj = await this.getEntityFromCache(vehicleId, VikingObject.getFactory());
      if (!(obj instanceof Vehicle)) return;

      if (obj.products?.some((vp) => vp.id === product.id)) return;

      this.addProductRefToVehicle(obj, product);
      await this.putEntity(obj, VikingObject.getFactory());
      await this.objects$.next(obj);
      await this.objects$.next(new SubscriberSentinel());
    } catch {
      // Vehicle may not exist in cache yet; ignore
    }
  }

  async addProduct(p: Product, firstPaymentPrice: number, trialVoucherId?: string): Promise<Product> {
    if (!this.cache) {
      throw new Error('Can not request payment without being signed in');
    }

    p.source = ProductSource.MyPages;

    let body: any = {};

    if (!trialVoucherId) {
      body = {
        data: p,
        extra: {
          paymentMethod: { type: 'Default' },
          firstPaymentPrice: firstPaymentPrice, // Price after discount.
        },
      };
    }

    if (trialVoucherId) {
      body = {
        data: p,
        extra: {
          paymentMethod: { type: 'Default' },
          firstPaymentPrice: firstPaymentPrice, // Price after discount.
          trialOptions: {
            discountId: trialVoucherId,
          },
        },
      };
    }

    const res = (await this.send('POST', Product.getUrl(p.countryCode), body, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const addedProduct = Product.getFactory().make(res.data);
    await this.putEntity(addedProduct, Product.getFactory()); // Update in db.

    if (res.extra && res.extra.payment) {
      const addedPayment = Payment.getFactory().make(res.extra.payment);
      await this.putEntity(addedPayment, Payment.getFactory()); // Update in db.
    }

    return addedProduct;
  }

  async submitProductStatus(url: string, product: Product): Promise<Product> {
    if (!this.cache) {
      throw new Error('Can not submit product status without being signed in');
    }
    if (!this.organizationCache) {
      throw new Error('Can not submit product status without organization');
    }
    if (!product) {
      throw new Error('Can not submit product status without the product');
    }

    const body = {
      data: ProductStatus.Ongoing,
      extra: {
        addCard: {
          acceptQueryParameters: url,
          note: undefined,
        },
      },
    };

    const res = (await this.send(
      'PUT',
      `/countries/${product.countryCode}/products/${product.id}/status`,
      body,
      this.cache,
    )) as any;

    return res.data.extra;
  }

  //#endregion

  //#region Set
  //#endregion

  //#region Delete

  async deletePaymentMethod(userId: string, p: PaymentMethod): Promise<PaymentMethod> {
    const res = await this.delete(PaymentMethod.getFactory(), p, PaymentMethod.getUrl(userId, p), this.paymentMethods$);

    if (!res) {
      throw new Error('Could not delete payment method');
    }

    const factory = PaymentMethod.getFactory();
    const obj = factory.make(res);

    await this.putEntity(obj, factory);

    return res;
  }

  private async delete<T extends Identifiable>(
    factory: Factory<T>,
    o: T,
    url: string,
    observable: AwaitingSubject<T>,
  ): Promise<T> {
    if (!o) {
      throw new Error('Object must be present.');
    }

    await this.isReady();

    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot add an object without being signed in.');
    }

    const res = (await this.send('DELETE', url, undefined, cache)) as DataResponse;
    if (!res.data) {
      throw new Error('Data not found.');
    }

    o = factory.make(res.data);
    await this.putEntity(o, factory); // Update in db.

    if (observable) {
      await observable.next(o);
      await observable.next(new SubscriberSentinel()); // Send signal that object has been sent.
    }

    return o;
  }

  //#endregion

  //#region Update

  private async edit<T extends Identifiable>(
    factory: Factory<T>,
    o: T,
    url: string,
    observable?: AwaitingSubject<T>,
  ): Promise<T> {
    if (!o) {
      throw new Error('Object must be present.');
    }

    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot edit an object without being signed in.');
    }

    await this.isReady();

    const res = (await this.send('PUT', url, { data: o }, cache)) as DataResponse;
    if (!res.data) {
      throw new Error('Data not found.');
    }

    o = factory.make(res.data);
    await this.putEntity(o, factory); // Update in db.

    if (observable) {
      await observable.next(o);
      await observable.next(new SubscriberSentinel()); // Send signal that object has been sent.
    }

    return o;
  }

  public async editUser(u: User): Promise<User> {
    return this.edit(User.getFactory(), u, User.getUrl(u.id), this.users$);
  }

  public async editUserConsent(userId: string, consent: string, opt: boolean): Promise<User> {
    const body = { data: opt };

    const res = (await this.send('PUT', `/users/${userId}/consent/${consent}`, body, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = User.getFactory();
    const user = factory.make(res.data);

    await this.putEntity(user, factory);

    return user;
  }

  public async editDefaultPaymentMethod(userId: string, paymentMethodId: string): Promise<User> {
    const body = {};

    const res = (await this.send(
      'PUT',
      `/users/${userId}/payment_methods/${paymentMethodId}/default`,
      body,
      this.cache,
    )) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = User.getFactory();
    const user = factory.make(res.data);

    await this.putEntity(user, factory);

    return user;
  }

  public async editInterest(i: Interest) {
    const body = { data: i };

    const res = (await this.send('PUT', Interest.getUrl(i.countryCode, i.id), body, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = Interest.getFactory();
    const obj = factory.make(res.data);

    await this.putEntity(obj, factory);

    return obj;
  }

  //#endregion

  //#region Add

  private async add<T extends Identifiable>(
    factory: Factory<T>,
    o: T,
    url: string,
    observable?: AwaitingSubject<T>,
    extra?: any,
  ): Promise<T> {
    if (!o) {
      throw new Error('Object must be present.');
    }

    await this.isReady();

    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot add an object without being signed in.');
    }

    const body = extra ? { data: o, extra: extra } : { data: o };

    const res = (await this.send('POST', url, body, cache)) as DataResponse;
    if (!res.data) {
      throw new Error('Data not found.');
    }

    o = factory.make(res.data);
    await this.putEntity(o, factory); // Update in db.

    if (observable) {
      await observable.next(o);
      await observable.next(new SubscriberSentinel()); // Send signal that object has been sent.
    }

    return o;
  }

  public async getBlob(url: string): Promise<Blob> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`);
    }
    return response.blob();
  }

  public async generatePaymentEventPdfLink(
    userId: string,
    paymentId: string,
    eventIndex: number,
  ): Promise<DataResponse> {
    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot generate PDF link without being signed in.');
    }

    const res = (await this.send(
      'POST',
      `/users/${userId}/payments/${paymentId}/event/${eventIndex}/pdf/link`,
      {},
      cache,
    )) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    return res;
  }

  public async generateInsuranceLetterPdfLink(
    userId: string,
    productId: string,
    periodIndex: number,
  ): Promise<DataResponse> {
    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot generate insurance letter PDF link without being signed in.');
    }

    const res = (await this.send(
      'POST',
      `/users/${userId}/products/${productId}/periods/${periodIndex}/insurance-letter/pdf/link`,
      {},
      cache,
    )) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    return res;
  }

  // Accepts Base64 encoded file string and returns full URL to file.
  public async addFile(base64File: string): Promise<string> {
    if (!base64File) {
      throw new Error("Can't upload PDF without a file.");
    }

    const c = this.cache;
    if (!c) {
      throw new Error("Can't add file without being signed in.");
    }

    const body = { data: base64File };

    const res = (await this.send('POST', '/file/upload', body, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    return res.data;
  }

  private async addImageToEntity<T extends Identifiable>(
    factory: Factory<T>,
    o: T,
    base64File: string,
    url: string,
    observable?: AwaitingSubject<T>,
  ): Promise<T> {
    if (!o) {
      throw new Error("Can't add image without an entity.");
    }

    if (!base64File) {
      throw new Error("Can't add image without file.");
    }

    await this.isReady();

    const body = { data: { data: base64File } };
    const res = (await this.send('POST', url + '/images', body, this.cache)) as DataResponse;

    if (!res.data) {
      throw new Error('No data when adding image to object.');
    }

    const newEntity: T = factory.make(res.data);
    await this.putEntity(newEntity, factory); // Update in db.

    if (observable) {
      await observable.next(newEntity);
      await observable.next(new SubscriberSentinel());
    }

    return newEntity;
  }

  public async addPaymentMethod(pm: PaymentMethod, a: PaymentMethodAction, userId: string): Promise<PaymentMethod> {
    const cache = this.cache;
    if (!cache) {
      throw new Error('Cannot add an object without being signed in.');
    }

    const res = (await this.send(
      'POST',
      `/users/${userId}/payment_methods`,
      { data: pm, extra: { action: a } },
      cache,
    )) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    const factory = PaymentMethod.getFactory();
    const newPaymentMethod = factory.make(res.data);
    await this.putEntity(newPaymentMethod, factory);
    await this.paymentMethods$.next(newPaymentMethod);
    await this.paymentMethods$.next(new SubscriberSentinel());
    return newPaymentMethod;
  }

  // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
  public async addSjekkpunktProduct(o: Object): Promise<void> {
    (await this.send('POST', '/countries/NO/products/register/sjekkpunkt', {
      data: o,
    })) as DataResponse;
  }

  async inviteUser(phoneNumber: string, language: LanguageCode) {
    if (!phoneNumber) {
      throw new Error('Trying to invite user without phone number');
    }

    const body = {
      data: phoneNumber,
      extra: { language: language },
    };

    const url = `/users/invite/phone/`;

    (await this.send('POST', url, body, this.cache)) as DataResponse;
  }

  public async addNewCard(u: User, language: string, currency: Currency, orgId: string): Promise<string> {
    const getAddCardUrl = {
      type: 'GetAddCardUrl',
      language: language,
      currency: currency,
      makeDefaultPaymentMethod: true,
    };

    const res = (await this.send(
      'POST',
      `/users/${u.id}/payment_methods`,
      {
        extra: {
          action: getAddCardUrl,
          organizationId: orgId,
        },
      },
      this.cache,
    )) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    return res.data;
  }

  // const action = {
  //   paymentMethodId: guid,
  //   acceptQueryParameters: url,
  // }

  public async addCard(id: string, url: string, userId: string, orgId: string): Promise<DataResponse> {
    const addCard = {
      type: 'AddCard',
      paymentMethodId: id,
      acceptQueryParameters: url,
      makeDefaultPaymentMethod: true,
    };

    const res = (await this.send(
      'POST',
      `/users/${userId}/payment_methods`,
      {
        extra: {
          action: addCard,
          organizationId: orgId,
        },
      },
      this.cache,
    )) as DataResponse;

    if (!res) {
      throw new Error('Data not found.');
    }

    return res;
  }

  /**
   * Request to add a card payment method to an organization.
   * POST organizations/{organizationId}/payment_methods with Card details.
   * Returns the Worldline (or provider) URL to redirect the user to complete card registration.
   * After the user completes the flow, the server callback adds the card to the organization.
   */
  public async addOrganizationPaymentMethodCard(
    organizationId: string,
    request: {
      issuingOrganizationId: string;
      makeDefault: boolean;
      language: string;
      currency: string;
    },
  ): Promise<string> {
    const body = {
      data: {
        issuingOrganizationId: request.issuingOrganizationId,
        makeDefault: request.makeDefault,
        details: {
          type: 'Card',
          language: request.language,
          currency: request.currency,
        },
      },
    };

    const res = (await this.send(
      'POST',
      `/organizations/${organizationId}/payment_methods`,
      body,
      this.cache,
    )) as DataResponse;

    if (res.data == null || typeof res.data !== 'string') {
      throw new Error('Expected redirect URL from add organization payment method.');
    }

    return res.data;
  }

  public async addUser(u: User, extra?: NewUserExtra): Promise<NewUserResponse> {
    if (!u) {
      throw new Error('Object must be present.');
    }

    const cache = this.cache;

    const res = (await this.send('POST', User.getUrl(), { data: u, extra: extra }, cache)) as DataResponse;

    if (!res.data) {
      throw new Error('Data not found.');
    }

    return new NewUserResponse(res.data);
  }

  //#endregion

  //#region Utils

  public async isReady(): Promise<void> {
    const self = this;
    return new Promise<void>(function (resolve) {
      const check = function () {
        if (self.db) {
          resolve();
          return;
        } else {
          setTimeout(check, 0); // Next slot in event loop.
        }
      };
      check();
    });
  }

  async sleep(milliseconds: number) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  //#endregion
}

//#region Helper classes

class UserCache {
  accessToken: string;
  refreshToken: string;
  userId: string;
  user?: User;
  polled: boolean;
  private finalInitialPollPackage?: number;
  private receivedInitialPollPackages: boolean[];

  constructor(accessToken: string, refreshToken: string, userId: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.userId = userId;
    this.polled = false;
    this.receivedInitialPollPackages = [];
  }

  setUser(u: User) {
    this.user = u;
  }

  public setFinalInitialPollPackage(finalInitialPollPackage: number) {
    if (!this.polled) {
      this.finalInitialPollPackage = finalInitialPollPackage;
    }
  }

  public setReceivedInitialPollPackage(receivedInitialPollPackage: number) {
    if (!this.polled) {
      this.receivedInitialPollPackages[receivedInitialPollPackage - 1] = true;

      if (this.finalInitialPollPackage) {
        // Check if done.
        let ok = true;
        for (let i = 0; i < this.finalInitialPollPackage; i++) {
          if (!this.receivedInitialPollPackages[i]) {
            ok = false;
            break;
          }
        }
        if (ok) {
          this.polled = true;
          this.receivedInitialPollPackages = [];
          this.finalInitialPollPackage = undefined;
        }
      }
    }
  }

  public async hasPolled(): Promise<void> {
    const self = this;
    return new Promise<void>(function (resolve) {
      const check = function () {
        if (self.polled) {
          resolve();
          return;
        } else {
          setTimeout(check, 0); // Next slot in event loop.
        }
      };
      check();
    });
  }
}

class OrganizationCache {
  organizationId: string;
  organization?: Organization;
  polled: boolean;
  private finalInitialPollPackage?: number | null;
  private receivedInitialPollPackages: boolean[];

  constructor(organizationId: string) {
    this.organizationId = organizationId;
    this.polled = false;
    this.finalInitialPollPackage = undefined;
    this.receivedInitialPollPackages = [];
  }

  setOrganization(o: Organization) {
    this.organization = o;
  }

  /**
   * Clears persisted org poll cursor state in concert with localStorage; use before Signin with empty
   * etag so 206 replay is tracked and awaited again (otherwise polled stays true and new chunks are ignored).
   */
  resetInitialPollTracking(): void {
    this.polled = false;
    this.finalInitialPollPackage = undefined;
    this.receivedInitialPollPackages = [];
  }

  setFinalInitialPollPackage(finalInitialPollPackage: number) {
    if (!this.polled) {
      this.finalInitialPollPackage = finalInitialPollPackage;
    }
  }

  setReceivedInitialPollPackage(receivedInitialPollPackage: number) {
    if (!this.polled) {
      this.receivedInitialPollPackages[receivedInitialPollPackage - 1] = true;

      if (this.finalInitialPollPackage) {
        // Check if done.
        let ok = true;
        for (let i = 0; i < this.finalInitialPollPackage; i++) {
          if (!this.receivedInitialPollPackages[i]) {
            ok = false;
            break;
          }
        }
        if (ok) {
          this.polled = true;
          this.receivedInitialPollPackages = [];
          this.finalInitialPollPackage = null;
        }
      }
    }
  }

  async hasPolled(): Promise<void> {
    const self = this;
    return new Promise<void>(function (resolve) {
      const check = function () {
        if (self.polled) {
          resolve();
          return;
        } else {
          setTimeout(check, 0); // Next slot in event loop.
        }
      };
      check();
    });
  }
}

class CustomerOrganizationCache {
  customerOrganizationId: string;
  customerOrganization?: Organization;
  polled: boolean;
  private finalInitialPollPackage?: number | null;
  private receivedInitialPollPackages: boolean[];

  constructor(customerOrganizationId: string) {
    this.customerOrganizationId = customerOrganizationId;
    this.polled = false;
    this.finalInitialPollPackage = undefined;
    this.receivedInitialPollPackages = [];
  }

  setOrganization(o: Organization) {
    this.customerOrganization = o;
  }

  setFinalInitialPollPackage(finalInitialPollPackage: number) {
    if (!this.polled) {
      this.finalInitialPollPackage = finalInitialPollPackage;
    }
  }

  setReceivedInitialPollPackage(receivedInitialPollPackage: number) {
    if (!this.polled) {
      this.receivedInitialPollPackages[receivedInitialPollPackage - 1] = true;
    }

    if (this.finalInitialPollPackage) {
      // Check if done.
      let ok = true;
      for (let i = 0; i < this.finalInitialPollPackage; i++) {
        if (!this.receivedInitialPollPackages[i]) {
          ok = false;
          break;
        }
      }
      if (ok) {
        this.polled = true;
        this.receivedInitialPollPackages = [];
        this.finalInitialPollPackage = null;
      }
    }
  }

  async hasPolled(): Promise<void> {
    const self = this;
    return new Promise<void>(function (resolve) {
      const check = function () {
        if (self.polled) {
          resolve();
          return;
        } else {
          setTimeout(check, 0); // Next slot in event loop.
        }
      };
      check();
    });
  }
}

interface SigninResponse {
  data: {
    accessToken: string;
    refreshToken: string;
    userId: string;
  };
}

export interface DataResponse {
  data: any;
  extra: any;
}

interface UserPollResponse {
  data: {
    user: any;
    products: any;
    paymentMethods: any;
    objects: any;
    payments: any;
    discounts: any;
    etag: string;
  };
  id: number;
  done: boolean;
}

interface UserPollErrorResponse {
  text: string;
}

export enum UserPollRequestType {
  Signin = 1,
  TokenRefresh = 2,
  ForcePoll = 3,
  Signout = 4,
}

export enum UserPollResponseType {
  Poll = 1,
  TokenRefresh = 2,
  Error = 3,
  ConnectionOk = 4,
  ConnectionError = 5,
}
//#endregion
